import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, Power, PowerOff, Mail, MessageCircle, 
  Clock, CheckCircle, XCircle, AlertCircle, Zap, RefreshCw,
  Send, Play, Pause, RotateCcw, Eye, Settings
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CommunicationManager = () => {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('templates');
  const [stages, setStages] = useState([]);
  const [nurturingQueue, setNurturingQueue] = useState([]);
  const [nurturingStats, setNurturingStats] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingTemplateId, setTestingTemplateId] = useState(null);
  const { toast } = useToast();

  const initialFormData = {
    name: '',
    description: '',
    channel: 'email',
    trigger_event: 'lead_created',
    is_active: true,
    tone: 'professional_friendly',
    personalization_level: 'advanced',
    subject_template: '',
    body_template: '',
    fallback_content: '',
    delay_minutes: 2,
    apply_to_sources: [],
    apply_to_services: [],
    // Automation fields
    target_stage: '',
    auto_send_enabled: false,
    auto_send_delay_days: 1,
    sequence_order: 0,
    repeat_enabled: false,
    repeat_interval_days: 7,
    max_sends: 1,
    stop_on_reply: true,
    stop_on_stage_change: true,
    // Contact/Footer options
    show_whatsapp_button: true,
    show_email_button: true,
    show_phone_button: true,
    show_consultant_footer: true
  };

  const [formData, setFormData] = useState(initialFormData);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const getToken = useCallback(() => {
    return localStorage.getItem('token') || localStorage.getItem('admin_token');
  }, []);

  useEffect(() => {
    fetchData();
    fetchStages();
  }, [activeTab]);

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/communication-templates/nurturing/stages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStages(response.data);
    } catch (error) {
      console.error('Failed to fetch stages:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (activeTab === 'templates') {
        const response = await axios.get(`${BACKEND_URL}/api/communication-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data);
      } else if (activeTab === 'logs-stats') {
        try {
          const logsResponse = await axios.get(`${BACKEND_URL}/api/communication-templates/logs/all?limit=50`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setLogs(logsResponse.data || []);
        } catch (logsError) {
          console.error('Failed to fetch communication logs:', logsError);
          setLogs([]);
        }
        
        try {
          const statsResponse = await axios.get(`${BACKEND_URL}/api/communication-templates/stats/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setStats(statsResponse.data);
        } catch (statsError) {
          console.error('Failed to fetch stats:', statsError);
        }
      } else if (activeTab === 'nurturing') {
        try {
          const [queueRes, statsRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/communication-templates/nurturing/queue?limit=100`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${BACKEND_URL}/api/communication-templates/nurturing/queue/stats`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          setNurturingQueue(queueRes.data || []);
          setNurturingStats(statsRes.data);
        } catch (error) {
          console.error('Failed to fetch nurturing data:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({
        title: "Error",
        description: "Failed to load data. Check console for details.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData(initialFormData);
    setAttachments([]);
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setModalMode('edit');
    setSelectedTemplate(template);
    setAttachments(template.attachments || []);
    setFormData({
      name: template.name,
      description: template.description || '',
      channel: template.channel,
      trigger_event: template.trigger_event,
      is_active: template.is_active,
      tone: template.tone,
      personalization_level: template.personalization_level,
      subject_template: template.subject_template || '',
      body_template: template.body_template || '',
      fallback_content: template.fallback_content || '',
      delay_minutes: template.delay_minutes,
      apply_to_sources: template.apply_to_sources || [],
      apply_to_services: template.apply_to_services || [],
      // Automation fields
      target_stage: template.target_stage || '',
      auto_send_enabled: template.auto_send_enabled || false,
      auto_send_delay_days: template.auto_send_delay_days ?? 1,
      sequence_order: template.sequence_order ?? 0,
      repeat_enabled: template.repeat_enabled || false,
      repeat_interval_days: template.repeat_interval_days ?? 7,
      max_sends: template.max_sends ?? 1,
      stop_on_reply: template.stop_on_reply !== false,
      stop_on_stage_change: template.stop_on_stage_change !== false,
      // Contact/Footer options
      show_whatsapp_button: template.show_whatsapp_button !== false,
      show_email_button: template.show_email_button !== false,
      show_phone_button: template.show_phone_button !== false,
      show_consultant_footer: template.show_consultant_footer !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = getToken();
      
      if (modalMode === 'create') {
        await axios.post(`${BACKEND_URL}/api/communication-templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Template created successfully"
        });
      } else {
        await axios.put(`${BACKEND_URL}/api/communication-templates/${selectedTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Template updated successfully"
        });
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save template",
        variant: "destructive"
      });
    }
  };

  const handleToggleStatus = async (templateId) => {
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/communication-templates/${templateId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Template status updated" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = getToken();
      await axios.delete(`${BACKEND_URL}/api/communication-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Template deleted successfully" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete template", variant: "destructive" });
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail || !testingTemplateId) return;
    
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/communication-templates/nurturing/test-send?template_id=${testingTemplateId}&test_email=${encodeURIComponent(testEmail)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: `Test email sent to ${testEmail}` });
      setShowTestModal(false);
      setTestEmail('');
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to send test email", 
        variant: "destructive" 
      });
    }
  };

  const handleCancelQueue = async (queueId) => {
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/communication-templates/nurturing/queue/${queueId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Email cancelled" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to cancel email", variant: "destructive" });
    }
  };

  const handleRetryQueue = async (queueId) => {
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/communication-templates/nurturing/queue/${queueId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Email re-queued" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to retry email", variant: "destructive" });
    }
  };

  // Attachment handlers
  const handleAttachmentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.mp4', '.mov', '.avi'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast({ title: "Invalid File Type", description: "Only PDF, images, and videos are allowed.", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "File size must be less than 10MB", variant: "destructive" });
      return;
    }

    try {
      setUploadingAttachment(true);
      const token = getToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/communication-templates/${selectedTemplate.id}/attachments`,
        uploadFormData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': undefined }, timeout: 30000 }
      );

      if (response.data.success) {
        setAttachments([...attachments, response.data.attachment]);
        toast({ title: "Success", description: "Attachment uploaded successfully" });
      }
    } catch (error) {
      toast({ title: "Upload Failed", description: error.response?.data?.detail || "Failed to upload attachment", variant: "destructive" });
    } finally {
      setUploadingAttachment(false);
      event.target.value = '';
    }
  };

  const handleAttachmentDelete = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;

    try {
      const token = getToken();
      await axios.delete(
        `${BACKEND_URL}/api/communication-templates/${selectedTemplate.id}/attachments/${attachmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      toast({ title: "Success", description: "Attachment deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete attachment", variant: "destructive" });
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Clear all communication logs? This cannot be undone.')) return;

    try {
      const token = getToken();
      await axios.delete(`${BACKEND_URL}/api/communication-templates/logs/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs([]);
      toast({ title: "Success", description: "All logs cleared" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to clear logs", variant: "destructive" });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircle size={16} color="#10b981" />;
      case 'pending': return <Clock size={16} color="#f59e0b" />;
      case 'failed': return <XCircle size={16} color="#ef4444" />;
      case 'cancelled': return <Pause size={16} color="#6b7280" />;
      default: return <AlertCircle size={16} color="#6b7280" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStageName = (stageValue) => {
    const stage = stages.find(s => s.value === stageValue);
    return stage ? stage.label : stageValue;
  };

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div>
          <h1 className="page-title">Communication Management</h1>
          <p className="page-subtitle">Manage automated email & nurturing sequences</p>
        </div>
        {activeTab === 'templates' && (
          <button onClick={handleCreate} className="btn btn-primary" data-testid="add-template-btn">
            <Plus size={16} />
            Add Template
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
        {[
          { id: 'templates', label: 'Templates', icon: Mail },
          { id: 'nurturing', label: 'Nurturing Queue', icon: Zap },
          { id: 'logs-stats', label: 'Logs & Stats', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            style={{
              padding: '0.5rem 1rem',
              background: activeTab === tab.id ? '#777db0' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#374151',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <RefreshCw size={32} className="animate-spin" style={{ color: '#777db0' }} />
        </div>
      )}

      {/* Templates Tab */}
      {!loading && activeTab === 'templates' && (
        <div className="card">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channel</th>
                  <th>Trigger</th>
                  <th>Automation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id} data-testid={`template-row-${template.id}`}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <strong>{template.name}</strong>
                          {template.attachments?.length > 0 && (
                            <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '9999px' }}>
                              📎 {template.attachments.length}
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{template.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {template.channel === 'email' ? <Mail size={16} /> : <MessageCircle size={16} />}
                        {template.channel}
                      </div>
                    </td>
                    <td>{template.trigger_event.replace(/_/g, ' ')}</td>
                    <td>
                      {template.auto_send_enabled ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                            <Zap size={12} /> Auto-Send ON
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Stage: {getStageName(template.target_stage)}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            After {template.auto_send_delay_days}d, Seq #{template.sequence_order}
                          </span>
                        </div>
                      ) : (
                        <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>Manual Only</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${template.is_active ? 'badge-success' : 'badge-secondary'}`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => { setTestingTemplateId(template.id); setShowTestModal(true); }}
                          className="btn-icon"
                          title="Send Test Email"
                          data-testid={`test-template-${template.id}`}
                        >
                          <Send size={16} />
                        </button>
                        <button onClick={() => handleToggleStatus(template.id)} className="btn-icon" title={template.is_active ? 'Deactivate' : 'Activate'}>
                          {template.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button onClick={() => handleEdit(template)} className="btn-icon" title="Edit" data-testid={`edit-template-${template.id}`}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(template.id)} className="btn-icon btn-danger" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {templates.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      No templates found. Click "Add Template" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Nurturing Queue Tab */}
      {!loading && activeTab === 'nurturing' && (
        <div>
          {/* Queue Stats */}
          {nurturingStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="mini-stat-card accent-orange">
                <div className="mini-stat-icon"><Clock size={20} /></div>
                <div className="mini-stat-info">
                  <p className="mini-stat-value">{nurturingStats.queue?.pending || 0}</p>
                  <p className="mini-stat-label">Pending</p>
                </div>
              </div>
              <div className="mini-stat-card accent-green">
                <div className="mini-stat-icon"><CheckCircle size={20} /></div>
                <div className="mini-stat-info">
                  <p className="mini-stat-value">{nurturingStats.queue?.sent || 0}</p>
                  <p className="mini-stat-label">Sent</p>
                </div>
              </div>
              <div className="mini-stat-card accent-red">
                <div className="mini-stat-icon"><XCircle size={20} /></div>
                <div className="mini-stat-info">
                  <p className="mini-stat-value">{nurturingStats.queue?.failed || 0}</p>
                  <p className="mini-stat-label">Failed</p>
                </div>
              </div>
              <div className="mini-stat-card accent-purple">
                <div className="mini-stat-icon"><Send size={20} /></div>
                <div className="mini-stat-info">
                  <p className="mini-stat-value">{nurturingStats.total_nurturing_emails_sent || 0}</p>
                  <p className="mini-stat-label">Total Sent</p>
                </div>
              </div>
            </div>
          )}

          {/* Queue Table */}
          <div className="card">
            <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Email Queue</h3>
              <button onClick={fetchData} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
                <RefreshCw size={16} />
              </button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>Template</th>
                    <th>Stage</th>
                    <th>Scheduled</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nurturingQueue.map((item) => (
                    <tr key={item.id} data-testid={`queue-row-${item.id}`}>
                      <td>
                        <div>
                          <strong>{item.lead_name}</strong>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{item.lead_email}</div>
                        </div>
                      </td>
                      <td>{item.template_name}</td>
                      <td><span className="badge badge-info">{getStageName(item.target_stage)}</span></td>
                      <td>{new Date(item.scheduled_at).toLocaleString()}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {getStatusIcon(item.status)}
                          <span style={{ textTransform: 'capitalize' }}>{item.status}</span>
                        </div>
                        {item.error && <div style={{ fontSize: '0.75rem', color: '#ef4444' }}>{item.error}</div>}
                      </td>
                      <td>
                        {item.status === 'pending' && (
                          <button onClick={() => handleCancelQueue(item.id)} className="btn-icon" title="Cancel">
                            <Pause size={16} />
                          </button>
                        )}
                        {item.status === 'failed' && (
                          <button onClick={() => handleRetryQueue(item.id)} className="btn-icon" title="Retry">
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {nurturingQueue.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        <Zap size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.3 }} />
                        <p>No emails in queue</p>
                        <p style={{ fontSize: '0.875rem' }}>Emails will appear here when leads enter nurturing stages</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Logs & Stats Tab */}
      {!loading && activeTab === 'logs-stats' && (
        <div>
          {/* Stats Section */}
          {stats && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>📊 Statistics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="mini-stat-card accent-purple">
                  <div className="mini-stat-icon"><Mail size={20} /></div>
                  <div className="mini-stat-info">
                    <p className="mini-stat-value">{stats.templates.total}</p>
                    <p className="mini-stat-label">Templates</p>
                    <p className="mini-stat-sublabel">{stats.templates.active} active</p>
                  </div>
                </div>
                <div className="mini-stat-card accent-green">
                  <div className="mini-stat-icon"><CheckCircle size={20} /></div>
                  <div className="mini-stat-info">
                    <p className="mini-stat-value">{stats.communications.sent}</p>
                    <p className="mini-stat-label">Sent</p>
                  </div>
                </div>
                <div className="mini-stat-card accent-orange">
                  <div className="mini-stat-icon"><Clock size={20} /></div>
                  <div className="mini-stat-info">
                    <p className="mini-stat-value">{stats.communications.pending}</p>
                    <p className="mini-stat-label">Pending</p>
                  </div>
                </div>
                <div className="mini-stat-card accent-red">
                  <div className="mini-stat-icon"><XCircle size={20} /></div>
                  <div className="mini-stat-info">
                    <p className="mini-stat-value">{stats.communications.failed}</p>
                    <p className="mini-stat-label">Failed</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logs Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>📝 Communication Logs</h3>
              {logs.length > 0 && (
                <button onClick={handleClearLogs} className="btn btn-secondary" style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: 'none' }}>
                  <Trash2 size={16} /> Clear All
                </button>
              )}
            </div>
            
            <div className="card">
              <div className="table-container">
                {logs.length > 0 ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Template</th>
                        <th>Channel</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Sent At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            <div>
                              <strong>{log.lead_name}</strong>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{log.lead_email}</div>
                            </div>
                          </td>
                          <td>{log.template_name}</td>
                          <td>{log.channel === 'email' ? <Mail size={16} /> : <MessageCircle size={16} />}</td>
                          <td>{log.subject || '-'}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {getStatusIcon(log.status)}
                              {log.status}
                            </div>
                          </td>
                          <td>{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af' }}>
                    <AlertCircle size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No communication logs yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Create Template' : 'Edit Template'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-body">
              {/* Basic Info Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Settings size={18} /> Basic Information
                </h3>
                
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="template-name-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  <div className="form-group">
                    <label className="form-label">Channel *</label>
                    <select className="form-input" value={formData.channel} onChange={(e) => setFormData({ ...formData, channel: e.target.value })}>
                      <option value="email">Email</option>
                      <option value="whatsapp" disabled>WhatsApp (Coming Soon)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Trigger Event *</label>
                    <select className="form-input" value={formData.trigger_event} onChange={(e) => setFormData({ ...formData, trigger_event: e.target.value })}>
                      <option value="lead_created">Lead Created</option>
                      <option value="lead_assigned">Lead Assigned</option>
                      <option value="stage_changed">Stage Changed</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  <div className="form-group">
                    <label className="form-label">Tone *</label>
                    <select className="form-input" value={formData.tone} onChange={(e) => setFormData({ ...formData, tone: e.target.value })}>
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="professional_friendly">Professional & Friendly</option>
                      <option value="enthusiastic">Enthusiastic</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Delay (minutes)</label>
                    <input type="number" className="form-input" value={formData.delay_minutes} onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) })} min="0" />
                  </div>
                </div>
              </div>

              {/* Email Content Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={18} /> Email Content
                </h3>
                
                <div className="form-group">
                  <label className="form-label">Subject Template</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., {name}, Your {service} Journey to {destination_country}"
                    value={formData.subject_template}
                    onChange={(e) => setFormData({ ...formData, subject_template: e.target.value })}
                    data-testid="subject-template-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Body Template (HTML)</label>
                  <textarea
                    className="form-input"
                    rows="6"
                    placeholder="<html><body><p>Dear {name},</p><p>We're excited to help with your {service}...</p></body></html>"
                    value={formData.body_template}
                    onChange={(e) => setFormData({ ...formData, body_template: e.target.value })}
                    data-testid="body-template-input"
                  />
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                    Variables: {'{name}'}, {'{first_name}'}, {'{email}'}, {'{service}'}, {'{destination_country}'}, {'{source_country}'}, {'{current_date}'}, {'{current_year}'}
                  </small>
                </div>
              </div>

              {/* Automation Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Zap size={18} color="#d97706" /> Lead Nurturing Automation
                </h3>
                
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.auto_send_enabled}
                      onChange={(e) => setFormData({ ...formData, auto_send_enabled: e.target.checked })}
                      data-testid="auto-send-checkbox"
                    />
                    <strong>Enable Auto-Send for Lead Nurturing</strong>
                  </label>
                  <small style={{ color: '#6b7280', marginLeft: '1.5rem' }}>
                    Automatically send this email to leads in the target stage
                  </small>
                </div>

                {formData.auto_send_enabled && (
                  <>
                    <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Target Stage *</label>
                        <select
                          className="form-input"
                          value={formData.target_stage}
                          onChange={(e) => setFormData({ ...formData, target_stage: e.target.value })}
                          required={formData.auto_send_enabled}
                          data-testid="target-stage-select"
                        >
                          <option value="">Select Stage...</option>
                          {stages.map(stage => (
                            <option key={stage.value} value={stage.value}>{stage.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Sequence Order</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.sequence_order}
                          onChange={(e) => setFormData({ ...formData, sequence_order: parseInt(e.target.value) })}
                          min="0"
                          placeholder="0 = first email"
                        />
                        <small style={{ color: '#6b7280' }}>Lower numbers send first</small>
                      </div>
                    </div>

                    <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                      <div className="form-group">
                        <label className="form-label">Days After Entering Stage</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.auto_send_delay_days}
                          onChange={(e) => setFormData({ ...formData, auto_send_delay_days: parseInt(e.target.value) })}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Max Sends</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.max_sends}
                          onChange={(e) => setFormData({ ...formData, max_sends: parseInt(e.target.value) })}
                          min="1"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.repeat_enabled}
                          onChange={(e) => setFormData({ ...formData, repeat_enabled: e.target.checked })}
                        />
                        <span>Repeat if lead stays in stage</span>
                      </label>
                    </div>

                    {formData.repeat_enabled && (
                      <div className="form-group">
                        <label className="form-label">Repeat Interval (days)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={formData.repeat_interval_days}
                          onChange={(e) => setFormData({ ...formData, repeat_interval_days: parseInt(e.target.value) })}
                          min="1"
                          style={{ maxWidth: '200px' }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.stop_on_reply}
                          onChange={(e) => setFormData({ ...formData, stop_on_reply: e.target.checked })}
                        />
                        <span>Stop on reply</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.stop_on_stage_change}
                          onChange={(e) => setFormData({ ...formData, stop_on_stage_change: e.target.checked })}
                        />
                        <span>Stop on stage change</span>
                      </label>
                    </div>
                  </>
                )}
              </div>

              {/* Contact & Footer Options Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MessageCircle size={18} color="#0284c7" /> Contact & Footer Options
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Configure which contact options appear in email footer and contact boxes
                </p>
                
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_consultant_footer}
                      onChange={(e) => setFormData({ ...formData, show_consultant_footer: e.target.checked })}
                      data-testid="show-consultant-footer-checkbox"
                    />
                    <strong>Show Consultant Details Footer</strong>
                  </label>
                  <small style={{ color: '#6b7280', marginLeft: '1.5rem' }}>
                    Display consultant name, title, and contact info at bottom of emails
                  </small>
                </div>

                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #bae6fd' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#0369a1', marginBottom: '0.75rem' }}>
                    Contact Box Buttons (for stages without specific CTAs):
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.show_whatsapp_button}
                        onChange={(e) => setFormData({ ...formData, show_whatsapp_button: e.target.checked })}
                        data-testid="show-whatsapp-checkbox"
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        💬 WhatsApp
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.show_phone_button}
                        onChange={(e) => setFormData({ ...formData, show_phone_button: e.target.checked })}
                        data-testid="show-phone-checkbox"
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        📞 Phone/Call
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.show_email_button}
                        onChange={(e) => setFormData({ ...formData, show_email_button: e.target.checked })}
                        data-testid="show-email-checkbox"
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        ✉️ Email
                      </span>
                    </label>
                  </div>
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                    WhatsApp opens directly in the app on mobile devices
                  </small>
                </div>
              </div>

              {/* Attachments Section (Edit mode only) */}
              {modalMode === 'edit' && selectedTemplate && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>📎 Attachments</h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <input type="file" id="attachment-upload" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi" onChange={handleAttachmentUpload} style={{ display: 'none' }} disabled={uploadingAttachment} />
                    <label htmlFor="attachment-upload" className="btn btn-secondary" style={{ cursor: uploadingAttachment ? 'not-allowed' : 'pointer', opacity: uploadingAttachment ? 0.6 : 1 }}>
                      <Plus size={16} /> {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
                    </label>
                  </div>

                  {attachments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {attachments.map((att) => (
                        <div key={att.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          <div>
                            <strong style={{ fontSize: '0.875rem' }}>{att.filename}</strong>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>{formatFileSize(att.file_size)}</span>
                          </div>
                          <button type="button" onClick={() => handleAttachmentDelete(att.id)} style={{ padding: '0.25rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No attachments. Upload files to include with this email.</p>
                  )}
                </div>
              )}

              {/* Active Status */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                  <strong>Active</strong>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="save-template-btn">
                  {modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Send Test Email</h2>
              <button onClick={() => setShowTestModal(false)} className="modal-close">&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Test Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email to receive test"
                  data-testid="test-email-input"
                />
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                A test email will be sent with sample lead data to verify your template.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowTestModal(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleTestEmail} className="btn btn-primary" disabled={!testEmail} data-testid="send-test-btn">
                <Send size={16} /> Send Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationManager;
